import { trackContactRequestFormCsEvent, trackPropertyInfoCsEvent } from 'app/client/analytics';
import { getContactRequestForm, getEnvelope, getPropertyInfo } from 'app/client/analytics/utils';
import type { HotpadsListing } from 'app/client/analytics/types';

// Valid input for frequently used triggers
export type ContactFormLeadTriggerObject =
  | 'home_details_component|contact_form'
  | 'home_details_component|inline_form'
  | 'home_details_component|modal_contact_form'
  | 'home_details_component|summary'
  | 'home_details|post_contact_modal'
  | 'property_card'
  | 'search_results|post_contact_modal';
export type PhoneLeadTriggerObject =
  | 'home_details_component|inline_form'
  | 'home_details_component|modal_contact_form'
  | 'home_details_component|summary'
  | 'home_details_component | modal_contact_form'
  | 'property_card';

export type LeadTriggerSource =
  | 'button_to_complete_contact_form'
  | 'button_to_complete_request_to_apply_form'
  | 'button_to_send_message';

export type ContactFormTriggerLocation =
  | 'home_details'
  | 'home_details_halo'
  | 'search_results'
  | 'search_results_feed_halo';

// https://dataportal.zillowgroup.net/event-registry/5568
export const emitTrackInlineContactFormViewEvent = ({ listing }: { listing: HotpadsListing | null | undefined }) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5568',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'impression',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'hdp_contact_form',
      trigger_object_nm: 'home_details_component|inline_form',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// Note (for Clickstream testing): No GA event associated with this.
export const emitTrackModalContactFormViewEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject = 'home_details_component|modal_contact_form',
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject?: ContactFormLeadTriggerObject;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5569',
      eventVersion: '2',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'impression',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'modal_contact_form',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5570
export const emitTrackInlineContactSubmitEvent = ({
  listing,
  rentalSubmitId,
  triggerLocation = 'home_details',
}: {
  listing: HotpadsListing | null | undefined;
  rentalSubmitId: string;
  triggerLocation?: ContactFormTriggerLocation;
}) => {
  trackContactRequestFormCsEvent({
    envelope: getEnvelope({
      eventId: '5570',
      eventVersion: '2',
      templateId: '68',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'button_to_complete_contact_form',
      trigger_object_nm: 'home_details_component|inline_form',
    },
    semantic: {
      semantic_event_nm: 'inline_contact_submit',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
    ...getContactRequestForm(rentalSubmitId),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5571
export const emitTrackModalContactSubmitEvent = ({
  listing,
  rentalSubmitId,
  triggerLocation,
  triggerObject,
  triggerSource,
}: {
  listing: HotpadsListing | null | undefined;
  rentalSubmitId: string;
  triggerLocation: ContactFormTriggerLocation;
  triggerObject: ContactFormLeadTriggerObject;
  triggerSource: LeadTriggerSource;
}) => {
  trackContactRequestFormCsEvent({
    envelope: getEnvelope({
      eventId: '5571',
      eventVersion: '5',
      templateId: '68',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: triggerSource,
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'modal_contact_submit',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
    ...getContactRequestForm(rentalSubmitId),
  });
};

/**
 * User clicks send message CTA on similar listings to contact multiple listings.
 * https://dataportal.zillowgroup.net/event-registry/7276
 */
export const emitTrackSimilarListingMultiContactMessageButtonClickEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject = 'home_details|post_contact_modal',
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject?: ContactFormLeadTriggerObject;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '7276',
      eventVersion: '3',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'button_to_send_message',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'send_message_similar_listing',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

/**
 * When user clicks Send Message on the similar listings post contact, this will track
 * individually how many listings were bulk contacted using that button.
 * https://dataportal.zillowgroup.net/event-registry/7283
 */
export const emitTrackSimilarListingMultiContactSubmitEvent = ({
  listing,
  rentalSubmitId,
  triggerLocation = 'home_details',
  triggerObject = 'home_details|post_contact_modal',
}: {
  listing: HotpadsListing | null | undefined;
  rentalSubmitId: string;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject?: ContactFormLeadTriggerObject;
}) => {
  trackContactRequestFormCsEvent({
    envelope: getEnvelope({
      eventId: '7283',
      eventVersion: '2',
      templateId: '68',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'button_to_send_message',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'similar_listing_contact_submit',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
    ...getContactRequestForm(rentalSubmitId),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5574
export const emitTrackRequestContactStartEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject = 'home_details_component|summary',
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject?: ContactFormLeadTriggerObject;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5574',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'button_to_open_contact_form',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'request_contact_start',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5575
export const emitTrackFreeListingLeadEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject,
  triggerSource,
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject: ContactFormLeadTriggerObject;
  triggerSource: LeadTriggerSource;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5575',
      eventVersion: '6',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: triggerSource,
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'lead_email_free',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5576
export const emitTrackPaidListingLeadEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject,
  triggerSource,
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject: ContactFormLeadTriggerObject;
  triggerSource: LeadTriggerSource;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5576',
      eventVersion: '6',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: triggerSource,
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'lead_email_paid',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5577
export const emitTrackDemotedListingLeadEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject,
  triggerSource,
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject: ContactFormLeadTriggerObject;
  triggerSource: LeadTriggerSource;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5577',
      eventVersion: '6',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: triggerSource,
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'lead_email_paid_demoted',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5597
export const emitTrackPhoneLeadForFreePropertyEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject,
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject: PhoneLeadTriggerObject;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5597',
      eventVersion: '3',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'link_to_phone_number',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'lead_phone_free',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5599
export const emitTrackPhoneLeadForDemotedPropertyEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject,
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject: PhoneLeadTriggerObject;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5599',
      eventVersion: '3',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'link_to_phone_number',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'lead_phone_paid_demoted',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5600
export const emitTrackPhoneLeadForPaidPropertyEvent = ({
  listing,
  triggerLocation = 'home_details',
  triggerObject,
}: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject: PhoneLeadTriggerObject;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5600',
      eventVersion: '4',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'link_to_phone_number',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'lead_phone_paid',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};
